import { A } from '@mobily/ts-belt';
import { Box, Carousel, Placeholder } from '@stitch-fix/mode-react';
import ShoppableOutfit, {
  type ShoppableOutfitProps,
} from '@stitch-fix/mode-react/ShoppableOutfit';
import { SeeItOnMe, VisionStatusProvider } from '@stitch-fix/vision-shared';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import type { Merge } from 'type-fest';
import {
  OutfitItemFragment,
  OutfitFragment,
} from '../../utils/hooks/getOutfitsBySku';
import {
  FixItemOutfitFragment,
  FixItemOutfitItemFragment,
} from '../../utils/hooks/getFixItemOutfits';
import { ModeStickerLabelTheme } from '../../types/definitions/vendor/graphql-schema.d';

const SEE_IT_ON_ME_ZOOM_FEATURE_FLAG = 'client.web.vision_see_it_on_me_zoom';

type ImageComponent = ShoppableOutfitProps['imageComponents'][number];
type XAnchor = ImageComponent['laydown']['xAnchor'];
type YAnchor = ImageComponent['laydown']['yAnchor'];
type OutfitItemLabel = NonNullable<ImageComponent['label']>;

type OutfitsCarouselProps = {
  loading: boolean;
  outfits: OutfitFragment[] | FixItemOutfitFragment[];
  source: string;
};

type ClarifiedOutfitItemFragment = Merge<
  OutfitItemFragment | FixItemOutfitItemFragment,
  {
    laydown: OutfitItemFragment['laydown'] & {
      xAnchor: XAnchor;
      yAnchor: YAnchor;
    };
  }
>;

/**
 * Loading Slides for the carousel's loading state.
 */
const loadingSlides = Array.from({ length: 5 }, (item, key) => (
  <Placeholder
    data-testid="loading-placeholder"
    variant="rect"
    width="100%"
    height={0}
    pt="100%"
    key={key}
  />
));

const themeMapping: Record<ModeStickerLabelTheme, OutfitItemLabel['theme']> = {
  NAVY: 'navy',
  WHITE: 'white',
  CITRUS: 'citrus',
  MINT: 'mint',
  // TODO: Update this when the correct theme enums are available: https://github.com/stitchfix/web-frontend/blob/main/packages/mode-react/src/ShoppableOutfit/types.ts#L21
  DEFAULT_INVERSE: 'navy',
  DEFAULT_LIGHT: 'white',
};

/**
 * Function that transforms Outfit Items into Image Components for the carousel to render
 */
const transformOutfitItemToImageComponent = (
  item: ClarifiedOutfitItemFragment,
): Omit<ImageComponent, 'url'> => ({
  id: item.shopProductVariant?.sku ?? '',
  imageUrl: item.shopProductVariant?.image?.imageUrl ?? '',
  altText: item.shopProductVariant?.image?.altText ?? '',
  label: item.sticker
    ? { text: item.sticker.label, theme: themeMapping[item.sticker.theme] }
    : undefined,
  laydown: {
    ...item.laydown,
    xAnchor: item.laydown.xAnchor,
    yAnchor: item.laydown.yAnchor,
  },
});

/**
 * Guard Type that is used by a filter to check if it is a
 * ClarifiedOutfitItem. It checks if the Y or X Anchor any of the
 * laydown anchors, if not then it will not include it as an outfitItem.
 */
const isClarifiedOutfitItemFragment = (
  outfitItem: OutfitItemFragment | FixItemOutfitItemFragment,
): outfitItem is ClarifiedOutfitItemFragment => {
  const xAnchor: XAnchor[] = ['left', 'right', 'center'];
  const yAnchor: YAnchor[] = ['top', 'bottom', 'center'];

  return (
    A.includes(xAnchor, outfitItem.laydown.xAnchor) &&
    A.includes(yAnchor, outfitItem.laydown.yAnchor)
  );
};

/**
 * A function creates an array of outfit elements based on the data that comes from the server
 */
const getOutfitSlides = (
  outfits: OutfitFragment[] | FixItemOutfitFragment[],
  source: string,
  enableZoom: boolean,
) =>
  outfits.map(outfit => {
    const imageComponents = outfit.items
      .filter(isClarifiedOutfitItemFragment)
      .map(item => {
        return {
          ...transformOutfitItemToImageComponent(item),
          url: `${item?.shopProductVariant?.url}&outfit_id=${outfit.id}&source=${source}`,
        };
      });

    return (
      <ShoppableOutfit
        key={outfit.id}
        imageComponents={imageComponents || []}
        outfitId={outfit.id}
        topLeftContent={
          <SeeItOnMe
            skuIds={outfit.items.map(
              item => item.shopProductVariant?.sku ?? '',
            )}
            eventData={{ sourceApp: 'categories-next-ui' }}
            enableZoom={enableZoom}
          />
        }
      />
    );
  });

/**
 * <OutfitsCarousel /> a resuable component that renders the carousel or placeholder slides
 * if it is in a loading state.
 */
export const OutfitsCarousel = ({
  loading,
  outfits,
  source,
}: OutfitsCarouselProps) => {
  const { featureFlags } = useFeatureFlags('client', [
    SEE_IT_ON_ME_ZOOM_FEATURE_FLAG,
  ]);
  const enableZoom = featureFlags?.[SEE_IT_ON_ME_ZOOM_FEATURE_FLAG] === true;
  const carousel = (
    <Carousel
      ariaLabel="outfits-carousel"
      gutterOverflow={{ sm: 1, md: 1.5, lg: 0 }}
      slideWidth={{ sm: '320px', md: '384px' }}
    >
      {loading ? loadingSlides : getOutfitSlides(outfits, source, enableZoom)}
    </Carousel>
  );

  return (
    <Box>
      {!loading ? (
        <VisionStatusProvider>{carousel}</VisionStatusProvider>
      ) : (
        carousel
      )}
    </Box>
  );
};
